import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { isNullOrUndefined } from '@sla-ng/core';
import { Moment } from 'moment';
import { debounceTime, distinctUntilChanged, Observable, Subject, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';
import { ICost } from '../../../models/cost.model';
import { TransactionalDataFacade } from '../../../services/facades/external-data/transactional-data-facade';
import { ITransactionalDataState } from '../../../services/facades/external-data/transactional-data.state.model';

@Component({
    templateUrl: './cost.page.html',
    styleUrls: [ './cost.page.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CostPage implements OnInit, AfterViewInit, OnDestroy {

    @ViewChild(MatSort)
    public sort: MatSort;
    @ViewChild(MatPaginator)
    public paginator: MatPaginator;

    public dataSource: MatTableDataSource<ICost> = new MatTableDataSource<ICost>([]);

    public transactionalDataState$: Observable<ITransactionalDataState>;
    public datefilterFormGroup: FormGroup = new FormGroup({ start: new FormControl<Moment>(null), end: new FormControl<Moment>(null) });
    private destroy$: Subject<void> = new Subject();

    constructor(private myTransactionalDataFacade: TransactionalDataFacade) {
    }

    public ngOnInit(): void {
        this.transactionalDataState$ = this.myTransactionalDataFacade.subState();
        this.transactionalDataState$
            .pipe(
                takeUntil(this.destroy$),
                map((s: ITransactionalDataState) => s.costs))
            .subscribe((data: ICost[]) => {
                this.dataSource.data = data;
            });
        this.datefilterFormGroup.valueChanges.pipe(debounceTime(500)).subscribe((daterange: { start: Moment, end: Moment }) => {
            if (!isNullOrUndefined(daterange.start) && !isNullOrUndefined(daterange.end)) {
                this.myTransactionalDataFacade.setDateRange(daterange);
                void this.myTransactionalDataFacade.fetchCosts();
            }
        });

        // Initialize the datepicker with the daterange from the state
        const state: ITransactionalDataState = this.myTransactionalDataFacade.getStateSnapshot();
        this.datefilterFormGroup.setValue(state.selectedDateRange);
        // Fetch CostTypes and CostCenters
        void this.myTransactionalDataFacade.fetchCostMappingData();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        // Remove Costs from memory
        this.myTransactionalDataFacade.clearCosts();
    }

    public ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;

        this.transactionalDataState$.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(
                (prev, curr) =>
                    prev.costTypeLookup === curr.costTypeLookup &&
                    prev.costCenterLookup === curr.costCenterLookup
            )
        ).subscribe((state: ITransactionalDataState) => {
            this.dataSource.sortingDataAccessor = (item: ICost, tableColumnName: string): string | number => {
                switch (tableColumnName) {
                    case 'costTypeName':
                        return item.ledgerAccountNr in state.costTypeLookup ? state.costTypeLookup[ item.ledgerAccountNr ].name : item.ledgerAccountNr;
                    case 'costCenterName':
                        return item.financialAccountingNr in state.costCenterLookup ? state.costCenterLookup[ item.financialAccountingNr ].name : item.financialAccountingNr;
                    default:
                        return item[ tableColumnName ];
                }
            };
        });
    }

    public applyFilter(filterValue: string): void {
        filterValue = filterValue.trim(); // Remove whitespace
        filterValue = filterValue.toLowerCase(); // MatTableDataSource defaults to lowercase matches
        this.dataSource.filter = filterValue;
    }

}
